import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion"; // Import Framer Motion
import { Minus, Plus } from "lucide-react";
import { useDispatch } from "react-redux";
import { addToCart } from "../store/cartSlice";
import { colors } from "../colors";
import { getMenuItemImage } from "./menuItemImageMapping";

const CartModal = ({ isOpen, onClose, item }) => {
  const sizeOptions = Object.keys(item.price);
  const [reviewNote, setReviewNote] = useState("");
  const [selectedExtraToppings, setSelectedExtraToppings] = useState([]);
  const [selectedSize, setSelectedSize] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [makeItAMeal, setMakeItAMeal] = useState(false);
  const [selectedSide, setSelectedSide] = useState("");
  const [selectedDrink, setSelectedDrink] = useState("");
  const [selectedCrispFlavor, setSelectedCrispFlavor] = useState("");

  const jacketClassicToppings = ["Cheese", "Tuna", "Butter", "Beans"];

  const drinkOptions = [
    "Pepsi 16oz 454 Ml",
    "Pepsi Max 16oz 454 Ml",
    "7up Sugar Free 16oz 454 Ml",
    "Tango 16oz 454 Ml",
    "Blueberry 454 Ml",
    "Water 500ml",
    "Diet Coke",
    "Coke Can"

  ];

  const sauces = [
    "Garlic",
    "Mayo",
    "Chipotle",
    "Honey Mustard",
    "Sweet Chilli",
    "Vegan Mayo",
    "PeriPeri",
    "BBQ",
    "Ketchup"
  ];

  const toppings = [
    "Lettuce",
    "Cucumber",
    "Tomato",
    "Red Onion",
    "Pickles",
    "Sweetcorn",
    "Mix Pepper",
    "Jalapeno",
    "Black Olives",
    "Cheese",
    "Extra Cheese"
  ];

  const meatToppings = [
    "Plain chicken",
    "Meatballs",
    "Chicken Tikka",
    "Chicken Teriyaki",
    "Salami",
    "Ham",
    "Pepperoni",
    "Shredded Beef",
    "Shawarma",
    "Chicken Strip",
    "Bacon",
    "Turkey",
    "Tuna",
    "Vegetable"
  ];

  const breadOptions = [
    "White",
    "WholeMeal",
    "MultiGrain",
    "White Bread Cheese & Herbs"
  ];
  // Extra pricing when selected at first
  const initialToppingPrices = {
    "Extra Cheese": 0.5,
    "Chicken Tikka": 0.5,
    "Shredded Beef": 0.5,
    "Chicken Shawarma": 1.49,
    "Meat Ball": 0.5,
  };

  // Extra toppings pricing (only show these in Extra Toppings list)
  const extraToppingPrices = {
    "Plain chicken": 1.5,
    "Meat Ball": 2,
    "Chicken Tikka": 2,
    "Chicken Teriyaki": 1.5,
    "Salami": 1.5,
    "Ham": 1.5,
    "Pepperoni": 1.5,
    "Shredded Beef": 2,
    "Chicken Shawarma": 2,
    "Chicken Strips": 2,
    "Bacon": 1.5,
    "Turkey": 1.5,
    "Tuna": 1.5,
  };

  const walkersOptions = [
    "Prawn Cocktail Crisp",
    "Ready Salted Crisp",
    "Salt and Vinegar",
    "Cheese and Onion"
  ];

  const dorittosOptions = [
    "Tangy Cheese",
    "Chilli Heatwave",
    "Cool Original"
  ];

  const sideOptions = ["Milk Chocolate Cookie", ...walkersOptions, ...dorittosOptions];


  const [quantity, setQuantity] = useState(1);
  const [selectedToppings, setSelectedToppings] = useState([]);
  const [selectedClassicToppings, setSelectedClassicToppings] = useState([]);
  const [selectedSauces, setSelectedSauces] = useState("");
  const [selectedMeatTopping, setSelectedMeatTopping] = useState("");
  const [selectedBread, setSelectedBread] = useState("White");
  const [goLarge, setGoLarge] = useState(false);
  const [doubleMeat, setDoubleMeat] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  const dispatch = useDispatch();

  // Check if current item type should show new options
  const shouldShowOptions = ["Breakfast", "Subs", "Salads", "Bowls", "Wraps"].includes(item.Type);
  const shouldShowBread = ["Breakfast", "Subs"].includes(item.Type);
  const shouldShowGoLarge = item.Type === "Subs";

  // Check if item title contains a meat topping name
  const matchedMeatTopping = meatToppings.find(meat =>
    item.title.toLowerCase().includes(meat.toLowerCase())
  );

  const filteredMeatToppings = matchedMeatTopping ? [matchedMeatTopping] : meatToppings;
  const shouldPreSelectMeat = !!matchedMeatTopping;

  // Get the specific image for this menu item using your mapping
  const itemImage = getMenuItemImage(item.title, item.image);

  const handleToppingToggle = (topping) => {
    setSelectedToppings(
      (prev) =>
        prev.includes(topping)
          ? prev.filter((t) => t !== topping)
          : [...prev, topping]
    );
  };

  const handleClassicToppingToggle = (topping) => {
    setSelectedClassicToppings(
      (prev) =>
        prev.includes(topping)
          ? prev.filter((t) => t !== topping)
          : [...prev, topping]
    );
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleSauceToggle = (sauce) => {
    setSelectedSauces(prev => prev === sauce ? "" : sauce);
  };

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const calculatePrice = () => {
    let basePrice = 0;
    if (
      item.title == "Rice Bowl" || item.Type == 'Subs' || item.Type === 'Breakfast'
    ) {
      basePrice = item.price[selectedSize] || 0;
    } else {
      basePrice = item.price["default"] || 0;
    }
    basePrice = Number(basePrice);

    // Inside calculatePrice(), after the existing logic, add:
    if (makeItAMeal) {
      basePrice += 1.5;
      // Add extra charge if Doritos flavor selected
      if (dorittosOptions.includes(selectedSide)) {
        basePrice += 0.5;
      }
    }

    // Add pricing for new options
    if (item.Type === "JackedPotato") {
      selectedExtraToppings.forEach((topping) => {
        // Skip free classics
        if (!jacketClassicToppings.includes(topping)) {
          basePrice += extraToppingPrices[topping] || 0;
        }
      });
    } else {
      // Existing logic for others
      if (selectedToppings.includes("Extra Cheese")) {
        basePrice += 0.5;
      }
      if (selectedMeatTopping === "Meat Ball") {
        basePrice += 0.5;
      }
      if (goLarge) {
        basePrice += 2.5;
      }
      if (doubleMeat) {
        basePrice += 1.5;
      }

      selectedExtraToppings.forEach((t) => {
        if (extraToppingPrices[t]) {
          basePrice += extraToppingPrices[t];
        }
      });
    }


    return basePrice;
  };

  const shouldShowJacketOptions = item.Type === "JackedPotato";

  const handleQuantityIncrease = () => {
    setQuantity((prev) => prev + 1);
  };
  const handleExtraToppingToggle = (topping) => {
    setSelectedExtraToppings((prev) =>
      prev.includes(topping)
        ? prev.filter((t) => t !== topping)
        : [...prev, topping]
    );
  };


  const handleQuantityDecrease = () => {
    setQuantity((prev) => Math.max(1, prev - 1));
  };

  const validateSelection = () => {
    const errors = [];
    if (item.Type === 'Crisps' && !selectedCrispFlavor) {
      errors.push("Please select a flavor");
    }

    if ((item.title === "Rice Bowl" || item.Type == 'Subs' || item.Type === 'Breakfast') && !selectedSize) {
      errors.push("Please select a size");
    }

    // Inside validateSelection(), add:
    if (makeItAMeal && !selectedDrink) {
      errors.push("Please select a drink for your meal");
    }



    if (item.Type === 'JackedPotato' && selectedClassicToppings.length == 0) {
      errors.push("Please select at least one classic topping");
    }

    return errors;
  };


  const handleAddToCart = () => {
    const errors = validateSelection();

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors([]);
    const descriptionParts = [];


    if (selectedSize) descriptionParts.push(`Size: ${selectedSize}`);
    if (item.Type === 'Crisps' && selectedCrispFlavor) {
      descriptionParts.push(`Flavor: ${selectedCrispFlavor}`);
    }
    if (makeItAMeal) {
      descriptionParts.push("Meal Deal");
      if (selectedSide) {
        let sideDisplayName = selectedSide;
        if (walkersOptions.includes(selectedSide)) {
          sideDisplayName = `Walkers ${selectedSide}`;
        } else if (dorittosOptions.includes(selectedSide)) {
          sideDisplayName = `Doritos ${selectedSide}`;
        }
        descriptionParts.push(`Side: ${sideDisplayName}`);
      }
      if (selectedDrink) descriptionParts.push(`Drink: ${selectedDrink}`);
    }
    // Add new options to description
    if (shouldShowOptions || shouldShowJacketOptions) {
      if (shouldShowBread && selectedBread) descriptionParts.push(`Bread: ${selectedBread}`);
      if (selectedMeatTopping) descriptionParts.push(`Meat: ${selectedMeatTopping}`);
      if (selectedClassicToppings.length > 0) descriptionParts.push(`Classic Toppings: ${selectedClassicToppings.join(", ")}`);
      if (selectedToppings.length > 0) descriptionParts.push(`Toppings: ${selectedToppings.join(", ")}`);
      if (selectedExtraToppings.length > 0) descriptionParts.push(`Extra Toppings: ${selectedExtraToppings.join(", ")}`);
      if (selectedSauces) descriptionParts.push(`Sauce: ${selectedSauces}`);

      if (goLarge) descriptionParts.push("Go Large");
      if (doubleMeat) descriptionParts.push("Double Meat");
    }



    if (reviewNote.trim())
      descriptionParts.push(`Review Note: ${reviewNote.trim()}`);

    const description = descriptionParts.join("\n");

    const cartItem = {
      id: item.id,
      title: item.title,
      image: item.image,
      description,
      itemQuantity: quantity,
      totalPrice: calculatePrice().toFixed(2),
      item_type: item.Type,
    };

    dispatch(addToCart(cartItem));
    onClose(); // Close modal
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);
  useEffect(() => {
    if (shouldPreSelectMeat && !selectedMeatTopping) {
      setSelectedMeatTopping(matchedMeatTopping);
    }
  }, [shouldPreSelectMeat, matchedMeatTopping, selectedMeatTopping]);

  if (!isOpen) return null;



  return (
    <motion.div
      className="fixed inset-0 flex items-center justify-center px-[5%] "
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "start",
        paddingLeft: "5%",
        zIndex: "110",
      }}
      transition={{ duration: 0.4 }} // Smooth fade-in/out for backdrop
    >
      <motion.div
        ref={modalRef}
        className="bg-white rounded-xl w-[100%] lg:w-[60%] relative shadow-xl max-h-[80vh] flex flex-col"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", stiffness: 250, damping: 25 }}
      >
        {/* Modal Header */}
        <div
          className="text-white px-4 lg:px-6 py-1 lg:py-4 flex justify-between items-center rounded-t-xl"
          style={{ backgroundColor: colors.primaryRed }}
        >
          <h2
            className="text-base lg:text-2xl"
            style={{ fontFamily: "Bambino", fontWeight: 650 }}
          >
            ADD ITEM
          </h2>
          <button onClick={onClose} className="text-white hover:text-gray-300">
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 lg:p-4 overflow-y-auto" style={{ flexGrow: 1 }}>
          <div className="grid grid-cols-2 lg:grid-cols-10 gap-4 mb-4">
            <div className="col-span-2">
              <motion.img
                src={itemImage}
                alt={item.title}
                className="w-auto h-16 lg:h-80 object-cover mr-4 rounded flex align-middle justify-center"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
            <div className="col-span-8 flex items-center justify-start">
              <div className="grid grid-cols-1">
                <p
                  className="font-semibold text-base lg:text-2xl text-start"
                  style={{ fontFamily: "Bambino", fontWeight: 450 }}
                >
                  {item.title}
                </p>
                {(item.title === "Rice Bowl" || item.Type == 'Subs' || item.Type === 'Breakfast') && (
                  <>
                    {/* Pizza Size */}
                    <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                      <div className="col-span-1">
                        <h3
                          className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                          style={{ fontFamily: "Bambino", fontWeight: 350 }}
                        >
                          Size
                        </h3>
                      </div>
                      <div className="col-span-1 lg:col-span-4 flex flex-wrap justify-start items-center gap-1">
                        {sizeOptions.map((size) => (
                          <motion.button
                            key={size}
                            onClick={() => handleSizeSelect(size)}
                            className={`px-2 lg:px-4 py-1 lg:py-1 text-sm lg:text-sm transition-colors rounded-lg  ${selectedSize === size
                              ? "bg-[#53BA4D] text-white"
                              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                              }`}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {size.toUpperCase()}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                    {validationErrors.length > 0 && (
                      <div className="col-span-full mb-2">
                        {validationErrors.map((error, index) => (
                          <p key={index} className="text-red-600 text-sm font-medium">
                            {error}
                          </p>
                        ))}
                      </div>
                    )}

                    {/* No Salad & No Sauce */}
                  </>
                )}

                {/* Crisps Flavors */}
                {item.Type === 'Crisps' && (
                  <>
                    {item.title.toLowerCase().includes('walkers') && (
                      <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                        <div className="col-span-1">
                          <h3
                            className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                            style={{ fontFamily: "Bambino", fontWeight: 350 }}
                          >
                            Walkers Flavors
                          </h3>
                        </div>
                        <div className="col-span-1 lg:col-span-4 flex flex-wrap justify-start items-center gap-1">
                          {walkersOptions.map((flavor) => (
                            <button
                              key={flavor}
                              onClick={() => setSelectedCrispFlavor(flavor)}
                              className={`px-2 lg:px-4 py-1 lg:py-1 text-sm lg:text-sm rounded transition-all duration-200 transform hover:scale-105 active:scale-95 ${selectedCrispFlavor === flavor
                                ? "bg-[#53BA4D] text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                }`}
                            >
                              {flavor}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {item.title.toLowerCase().includes('dorittos') && (
                      <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                        <div className="col-span-1">
                          <h3
                            className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                            style={{ fontFamily: "Bambino", fontWeight: 350 }}
                          >
                            Doritos Flavors
                          </h3>
                        </div>
                        <div className="col-span-1 lg:col-span-4 flex flex-wrap justify-start items-center gap-1">
                          {dorittosOptions.map((flavor) => (
                            <button
                              key={flavor}
                              onClick={() => setSelectedCrispFlavor(flavor)}
                              className={`px-2 lg:px-4 py-1 lg:py-1 text-sm lg:text-sm rounded transition-all duration-200 transform hover:scale-105 active:scale-95 ${selectedCrispFlavor === flavor
                                ? "bg-[#53BA4D] text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                }`}
                            >
                              {flavor}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Make It A Meal - Show for Subs, Wraps, Salads, Bowls */}
                {["Subs", "Wraps", "Salads", "Bowls"].includes(item.Type) && (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-1 pt-2">
                      <div className="col-span-1">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={makeItAMeal}
                            onChange={(e) => {
                              setMakeItAMeal(e.target.checked);
                              if (!e.target.checked) {
                                setSelectedSide("");
                                setSelectedDrink("");
                              }
                            }}
                            className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                          />
                          <h3
                            className="text-base lg:text-base font-semibold mb-0 flex items-center"
                            style={{ fontFamily: "Bambino", fontWeight: 350 }}
                          >
                            Make It A Meal (+£1.50)
                          </h3>
                        </label>
                      </div>
                      <div className="col-span-1 lg:col-span-4">
                        {/* Empty or you can remove this div */}
                      </div>
                    </div>

                    {/* Side Options - Show only if Make It A Meal is checked */}
                    {makeItAMeal && (
                      <>
                        <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                          <div className="col-span-1">
                            <h3
                              className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                              style={{ fontFamily: "Bambino", fontWeight: 350 }}
                            >
                              Side (Optional)
                            </h3>
                          </div>
                          <div className="col-span-1 lg:col-span-4 flex flex-wrap justify-start items-center gap-1">
                            {sideOptions.map((side) => {
                              let displayName = side;
                              if (walkersOptions.includes(side)) {
                                displayName = `Walkers ${side}`;
                              } else if (dorittosOptions.includes(side)) {
                                displayName = `Doritos ${side}`;
                              }

                              return (
                                <button
                                  key={side}
                                  onClick={() => setSelectedSide(selectedSide === side ? "" : side)}
                                  className={`px-2 lg:px-4 py-1 lg:py-1 text-sm lg:text-sm rounded transition-all duration-200 transform hover:scale-105 active:scale-95 ${selectedSide === side
                                    ? "bg-[#53BA4D] text-white"
                                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                    }`}
                                >
                                  {displayName}{dorittosOptions.includes(side) ? " (+£0.50)" : ""}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                          <div className="col-span-1">
                            <h3
                              className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                              style={{ fontFamily: "Bambino", fontWeight: 350 }}
                            >
                              Drink *
                            </h3>
                          </div>
                          <div className="col-span-1 lg:col-span-4 flex flex-wrap justify-start items-center gap-1">
                            {drinkOptions.map((drink) => (
                              <button
                                key={drink}
                                onClick={() => setSelectedDrink(drink)}
                                className={`px-2 lg:px-4 py-1 lg:py-1 text-sm lg:text-sm rounded transition-all duration-200 transform hover:scale-105 active:scale-95 ${selectedDrink === drink
                                  ? "bg-[#53BA4D] text-white"
                                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                  }`}
                              >
                                {drink}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                {shouldShowJacketOptions && (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                      <div className="col-span-1">
                        <h3
                          className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                          style={{ fontFamily: "Bambino", fontWeight: 350 }}
                        >
                          Classic Toppings (Free)
                        </h3>
                      </div>
                      <div className="col-span-1 lg:col-span-4 flex flex-wrap justify-start items-center gap-1">
                        {jacketClassicToppings.map((topping) => (
                          <button
                            key={topping}
                            onClick={() => handleClassicToppingToggle(topping)}
                            className={`px-2 lg:px-4 py-1 lg:py-1 text-sm rounded transition-all duration-200 ${selectedClassicToppings.includes(topping)
                              ? "bg-[#53BA4D] text-white"
                              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                              }`}
                          >
                            {topping}
                          </button>
                        ))}
                      </div>
                    </div>
                    {validationErrors.length > 0 && (
                      <div className="col-span-full mb-2">
                        {validationErrors.map((error, index) => (
                          <p key={index} className="text-red-600 text-sm font-medium">
                            {error}
                          </p>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* 🥔 JackedPotato - Paid Toppings (same prices as others, always enabled) */}
                {shouldShowJacketOptions && (
                  <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                    <div className="col-span-1">
                      <h3
                        className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                        style={{ fontFamily: "Bambino", fontWeight: 350 }}
                      >
                        Extra Toppings
                      </h3>
                    </div>
                    <div className="col-span-1 lg:col-span-4 flex flex-wrap justify-start items-center gap-1">
                      {Object.keys(extraToppingPrices).map((topping) => (
                        <button
                          key={topping}
                          onClick={() => handleExtraToppingToggle(topping)}
                          className={`px-2 lg:px-4 py-1 lg:py-1 text-sm rounded transition-all duration-200 ${selectedExtraToppings.includes(topping)
                            ? "bg-[#53BA4D] text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                        >
                          {topping} (+£{extraToppingPrices[topping]})
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bread Selection - Show for Breakfast and Subs */}
                {shouldShowBread && (
                  <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                    <div className="col-span-1">
                      <h3
                        className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                        style={{ fontFamily: "Bambino", fontWeight: 350 }}
                      >
                        Bread
                      </h3>
                    </div>
                    <div className="col-span-1 lg:col-span-4 flex flex-wrap justify-start items-center gap-1">
                      {breadOptions.map((bread) => (
                        <button
                          key={bread}
                          onClick={() => setSelectedBread(bread)}
                          className={`px-2 lg:px-4 py-1 lg:py-1 text-sm lg:text-sm rounded transition-all duration-200 transform hover:scale-105 active:scale-95 ${selectedBread === bread
                            ? "bg-[#53BA4D] text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                        >
                          {bread}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Meat Toppings - Show for applicable item types */}
                {shouldShowOptions && (
                  <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                    <div className="col-span-1">
                      <h3
                        className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                        style={{ fontFamily: "Bambino", fontWeight: 350 }}
                      >
                        Meat Toppings
                      </h3>
                    </div>
                    <div className="col-span-1 lg:col-span-4 flex flex-wrap justify-start items-center gap-1">
                      {filteredMeatToppings.map((meat) => (
                        <button
                          key={meat}
                          onClick={() => setSelectedMeatTopping(selectedMeatTopping === meat ? "" : meat)}
                          className={`px-2 lg:px-4 py-1 lg:py-1 text-sm lg:text-sm rounded transition-all duration-200 transform hover:scale-105 active:scale-95 ${selectedMeatTopping === meat
                            ? "bg-[#53BA4D] text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                        >
                          {meat}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Toppings - Show for applicable item types */}
                {(shouldShowJacketOptions || shouldShowOptions) && (
                  <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                    <div className="col-span-1">
                      <h3
                        className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                        style={{ fontFamily: "Bambino", fontWeight: 350 }}
                      >
                        Toppings
                      </h3>
                    </div>
                    <div className="col-span-1 lg:col-span-4 flex flex-wrap justify-start items-center gap-1">
                      {toppings.map((topping) => (
                        <button
                          key={topping}
                          onClick={() => handleToppingToggle(topping)}
                          className={`px-2 lg:px-4 py-1 lg:py-1 text-sm lg:text-sm rounded transition-all duration-200 transform hover:scale-105 active:scale-95 ${selectedToppings.includes(topping)
                            ? "bg-[#53BA4D] text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                        >
                          {topping}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Extra Toppings - show only if relevant items are selected initially */}
                {/* Extra Toppings - Show full list, disabled unless a meat topping is selected */}
                {shouldShowOptions && (
                  <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                    <div className="col-span-1">
                      <h3
                        className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                        style={{ fontFamily: "Bambino", fontWeight: 350 }}
                      >
                        Extra Toppings
                      </h3>
                    </div>
                    <div className="col-span-1 lg:col-span-4 flex flex-wrap justify-start items-center gap-1">
                      {Object.keys(extraToppingPrices).map((topping) => {
                        const isDisabled = !selectedMeatTopping && !shouldPreSelectMeat; // disable if no meat topping chosen
                        const isSelected = selectedExtraToppings.includes(topping);

                        return (
                          <button
                            key={topping}
                            onClick={() => !isDisabled && handleExtraToppingToggle(topping)}
                            disabled={isDisabled}
                            className={`px-2 lg:px-4 py-1 lg:py-1 text-sm lg:text-sm rounded transition-all duration-200 transform
              ${isSelected
                                ? "bg-[#53BA4D] text-white"
                                : isDisabled
                                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                              }`}
                          >
                            {topping} (+£{extraToppingPrices[topping]})
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Sauces - Show for applicable item types */}
                {(shouldShowJacketOptions || shouldShowOptions) && (
                  <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                    <div className="col-span-1">
                      <h3
                        className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                        style={{ fontFamily: "Bambino", fontWeight: 350 }}
                      >
                        Sauces
                      </h3>
                    </div>
                    <div className="col-span-1 lg:col-span-4 flex flex-wrap justify-start items-center gap-1">
                      {sauces.map((sauce) => (
                        <motion.button
                          key={sauce}
                          onClick={() => handleSauceToggle(sauce)}
                          className={`px-2 lg:px-4 py-1 lg:py-1 text-sm lg:text-sm  transition-colors rounded-lg  ${selectedSauces === sauce
                            ? "bg-[#53BA4D] text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {sauce}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Checkboxes for Go Large and Double Meat */}
                {shouldShowOptions && (
                  <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                    <div className="col-span-1">
                      <h3
                        className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                        style={{ fontFamily: "Bambino", fontWeight: 350 }}
                      >
                        Options
                      </h3>
                    </div>
                    <div className="col-span-1 lg:col-span-4 flex flex-wrap justify-start items-center gap-4">
                      {/* {shouldShowGoLarge && (
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={goLarge}
                            onChange={(e) => setGoLarge(e.target.checked)}
                            className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                          />
                          <span className="text-sm lg:text-sm">Go Large (+£2.50)</span>
                        </label>
                      )} */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={doubleMeat}
                          onChange={(e) => setDoubleMeat(e.target.checked)}
                          className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className="text-sm lg:text-sm">Double Meat (+£1.50)</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Quantity */}
                <div className="grid grid-cols-1 lg:grid-cols-5 pt-4">
                  <div className="col-span-1">
                    <h3
                      className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                      style={{ fontFamily: "Bambino", fontWeight: 350 }}
                    >
                      Quantity
                    </h3>
                  </div>
                  <div className="col-span-4 flex items-center space-x-2">
                    <motion.button
                      onClick={handleQuantityDecrease}
                      className="px-2 lg:px-4 py-1 lg:py-1 text-sm lg:text-sm bg-gray-300 text-gray-700 rounded"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Minus />
                    </motion.button>
                    <span className="text-sm lg:text-base">{quantity}</span>
                    <motion.button
                      onClick={handleQuantityIncrease}
                      className="px-2 lg:px-4 py-1 lg:py-1 text-sm lg:text-sm bg-gray-300 text-gray-700 rounded"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Plus />
                    </motion.button>
                  </div>
                </div>

                {/* Review Note */}
                <div className="grid grid-cols-1 lg:grid-cols-5 pt-2">
                  <div className="col-span-1">
                    <h3
                      className="text-base lg:text-base font-semibold mb-1 lg:mb-2 flex justify-start items-center py-1"
                      style={{ fontFamily: "Bambino", fontWeight: 350 }}
                    >
                      Review Note
                    </h3>
                  </div>
                  <div className="col-span-4">
                    <textarea
                      value={reviewNote}
                      onChange={(e) => setReviewNote(e.target.value)}
                      className="w-full p-2 border rounded text-sm"
                      placeholder="Write any special instructions or notes..."
                      rows={1}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="bg-gray-200 px-4 lg:px-6 py-2 lg:py-4 flex justify-between items-center rounded-b-xl">
          <div className="text-sm lg:text-lg font-semibold">
            Total: £{(calculatePrice() * quantity).toFixed(2)}
          </div>
          <div>
            <motion.button
              onClick={onClose}
              className="bg-gray-300 text-gray-800 text-sm lg:text-base px-1 lg:px-4 py-2 lg:py-2 rounded mr-2"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              style={{ fontFamily: "Bambino", fontWeight: 450 }}
            >
              Cancel
            </motion.button>
            <motion.button
              onClick={handleAddToCart}
              className="text-white px-2 lg:px-4 py-1 lg:py-1 rounded text-sm lg:text-base"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              style={{
                fontFamily: "Bambino",
                fontWeight: 450,
                backgroundColor: colors.primaryGreen,
              }}
            >
              Add to Cart
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CartModal;